// SyntheticUser (FNP-861) — the roster of the synthetic-user fleet.
//
// FNP-861 declared this module read-only, deferring retireSyntheticUser to "the
// lifecycle machinery". FNP-969 corrects that: a sweep of src/canary, src/tick and
// src/triage in flexinet-testing-api finds `retireSyntheticUser` ONLY in comments —
// no call site exists, so the machinery that was meant to own it was never built
// and the capability was reachable from nowhere. The console now offers it.
//
// createSyntheticUser and updateSyntheticUser remain out of scope, and there is
// still NO delete — retire tombstones. See services/mutations.ts.
//
// Mirrors the testing-api schema exactly:
//   type SyntheticUser {
//     id: ID!  personaId: ID!  email: AWSEmail!  secretArn: String!  accountId: ID
//     cognitoSub: ID  bornAt: AWSDateTime!  plannedLifespanDays: Int!
//     nextWakeAt: AWSDateTime  lifecycleStage: SyntheticUserLifecycleStage!
//     state: SyntheticUserState!  createdByUsername: ID  updatedByUsername: ID
//     created: AWSDateTime  updated: AWSDateTime
//   }

export enum SyntheticUserState {
    AWAKE = 'AWAKE',
    PAUSED = 'PAUSED',
    // FNP-848: a synthetic user row OUTLIVES the account it describes. A tombstone is
    // the record of a user that existed — it must render legibly and be clearly
    // marked, never hidden and never made to look like a broken row.
    TOMBSTONED = 'TOMBSTONED',
}

export enum SyntheticUserLifecycleStage {
    PROVISIONING = 'PROVISIONING',
    ACTIVE = 'ACTIVE',
    LAPSING = 'LAPSING',
    CANCELLED = 'CANCELLED',
    DELETED = 'DELETED',
}

export type SyntheticUser = {
    id: string,
    personaId: string,
    email: string,
    // NOTE: `secretArn` is deliberately ABSENT from this type and from every query
    // in this module. The row carries a REFERENCE to a credential, not a credential.
    // Do not add it, do not select it, and add no affordance that resolves it.
    accountId?: string | null,
    cognitoSub?: string | null,
    bornAt: string,
    plannedLifespanDays: number,
    // THE operational field — "who wakes next" is the question you ask when nothing
    // appears to be happening. Nullable: a tombstoned or paused user has no next wake.
    nextWakeAt?: string | null,
    lifecycleStage: SyntheticUserLifecycleStage,
    state: SyntheticUserState,
    createdByUsername?: string,
    updatedByUsername?: string,
    created?: string,
    updated?: string,
};

export type ListSyntheticUsers = {
    listSyntheticUsers: { items: SyntheticUser[], nextToken?: string }
}

export type GetSyntheticUser = {
    getSyntheticUser: SyntheticUser
}

// The narrow retire response — only the fields that change when a row becomes a
// tombstone. Not a full SyntheticUser: the mutation deliberately selects a subset.
export type RetireSyntheticUser = {
    retireSyntheticUser: Pick<SyntheticUser, 'id' | 'lifecycleStage' | 'state' | 'nextWakeAt' | 'updated'>
}

// SyntheticUsersFilterInput — the three fields the backend supports.
export type SyntheticUsersFilter = {
    personaId?: string,
    lifecycleStage?: string,
    state?: string,
};
